<template>
  <Header/>
  <div class="container is-fluid forbrug-page">
    <h1>{{ pageTitle }}</h1>

    <ForbrugFilters
      v-if="canView"
      :period="selectedPeriod"
      :vehicle="selectedVehicle"
      :account="selectedAccount"
      :period-options="periodOptions"
      :vehicles="vehicles"
      :accounts="accounts"
      :is-admin="isAdmin"
      :dirty="filtersDirty"
      @update:period="selectedPeriod = $event"
      @update:vehicle="selectedVehicle = $event"
      @update:account="selectedAccount = $event"
      @change="onFiltersChange"
      @reset="resetFilters"
      @open-share-links="invoiceMailsOpen = true"
    />

    <ForbrugInvoiceMailsModal
      :open="invoiceMailsOpen"
      @close="invoiceMailsOpen = false"
    />

    <div v-if="loadError" class="notification is-danger">{{ loadError }}</div>
    <div v-if="kmEditError" class="notification is-danger">{{ kmEditError }}</div>
    <p v-if="loading && !loadError">Henter forbrug…</p>

    <ForbrugTable
      v-if="canView && !loading"
      :usages="usages"
      :subscription="subscription"
      :is-admin="isAdmin"
      :vehicle-filter="selectedVehicle"
      :km-edit-booking-id="kmEditBookingId"
      :km-edit-field="kmEditField"
      :km-edit-value="kmEditValue"
      :km-edit-dirty="kmEditDirty"
      :km-edit-saving="kmEditSaving"
      @start-km-edit="startKmEdit"
      @update:km-edit-value="kmEditValue = $event"
      @save-km-edit="saveKmEdit"
      @cancel-km-edit="cancelKmEdit"
    />

    <LoginStatus />
  </div>
  <Footer/>
</template>

<script>
import { defineComponent } from 'vue'
import { useComposition } from '@/compositions/composition'

import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import LoginStatus from '@/components/LoginStatus.vue'
import ForbrugFilters from '@/components/forbrug/ForbrugFilters.vue'
import ForbrugTable from '@/components/forbrug/ForbrugTable.vue'
import ForbrugInvoiceMailsModal from '@/components/forbrug/ForbrugInvoiceMailsModal.vue'

import { fetchConsumption, saveKmCorrection } from '@/forbrug/consumptionApi'
import { buildPageTitle } from '@/forbrug/formatters'
import {
  buildPeriodOptions,
  parsePeriodValue,
  periodValue,
  previousMonthParts,
  startParam
} from '@/forbrug/period'

export default defineComponent({
  name: 'ForbrugPage',
  components: {
    Header,
    Footer,
    LoginStatus,
    ForbrugFilters,
    ForbrugTable,
    ForbrugInvoiceMailsModal
  },
  setup() {
    const { global } = useComposition()
    return { global }
  },
  data() {
    const prev = previousMonthParts()
    return {
      selectedYear: prev.year,
      selectedMonth: prev.month,
      selectedVehicle: '',
      selectedAccount: '',
      usages: [],
      vehicles: [],
      accounts: [],
      subscription: null,
      loading: false,
      loadError: '',
      loadedOnce: false,
      kmEditBookingId: null,
      kmEditField: '',
      kmEditValue: '',
      kmEditOriginal: '',
      kmEditSaving: false,
      kmEditError: '',
      invoiceMailsOpen: false
    }
  },
  created() {
    this.applyQueryToFilters()
    this.ensureAccessAndLoad()
  },
  computed: {
    isLoggedIn() {
      return this.global.user && Object.prototype.hasOwnProperty.call(this.global.user, 'id')
    },
    isAdmin() {
      return this.isLoggedIn && Number(this.global.user.accessLvl) === 1
    },
    accountFromQuery() {
      const account = this.$route.query && this.$route.query.account
      return account ? String(account) : ''
    },
    isSharedView() {
      return !this.isLoggedIn && !!(this.selectedAccount || this.accountFromQuery)
    },
    canView() {
      return this.isLoggedIn || this.isSharedView
    },
    periodOptions() {
      return buildPeriodOptions(12)
    },
    selectedPeriod: {
      get() {
        return periodValue(this.selectedYear, this.selectedMonth)
      },
      set(value) {
        const parsed = parsePeriodValue(value)
        if (!parsed) return
        this.selectedYear = parsed.year
        this.selectedMonth = parsed.month
      }
    },
    pageTitle() {
      return buildPageTitle({
        selectedYear: this.selectedYear,
        selectedMonth: this.selectedMonth,
        subscription: this.subscription,
        isAdmin: this.isAdmin,
        accountName: this.global.user && this.global.user.accountName
      })
    },
    startParam() {
      return startParam(this.selectedYear, this.selectedMonth)
    },
    filtersDirty() {
      const prev = previousMonthParts()
      const periodDirty = this.selectedYear !== prev.year || this.selectedMonth !== prev.month
      const vehicleDirty = this.selectedVehicle !== ''
      if (this.isSharedView) {
        return periodDirty || vehicleDirty
      }
      return periodDirty
        || vehicleDirty
        || this.selectedAccount !== ''
    },
    kmEditDirty() {
      if (this.kmEditBookingId === null) return false
      return String(this.kmEditValue).trim() !== String(this.kmEditOriginal)
    },
    accountParamForApi() {
      if (this.selectedAccount) {
        return this.selectedAccount
      }
      return ''
    }
  },
  watch: {
    isLoggedIn() {
      this.loadedOnce = false
      this.ensureAccessAndLoad()
    }
  },
  methods: {
    ensureAccessAndLoad() {
      if (!this.selectedAccount && this.accountFromQuery) {
        this.selectedAccount = this.accountFromQuery
      }
      // Members: keep household guid in URL so the page stays shareable (legacy forbrug.php)
      if (
        this.isLoggedIn
        && !this.isAdmin
        && !this.selectedAccount
        && this.global.user
        && this.global.user.accountGuid
      ) {
        this.selectedAccount = this.global.user.accountGuid
      }
      if (this.canView) {
        if (!this.loadedOnce) {
          this.syncFiltersToQuery()
          this.loadUsages()
        }
        return
      }
      // Wait for auth to settle; never bounce share links (?account=) away
      if (this.accountFromQuery) return
      if (this.global.user && this.global.user.authentificated === false) {
        this.$router.replace('/')
      }
    },
    startKmEdit(row, field) {
      if (!this.isAdmin || this.kmEditSaving) return
      if (this.kmEditBookingId === row.id && this.kmEditField === field) return
      if (this.kmEditBookingId !== null) {
        this.cancelKmEdit()
      }
      this.kmEditError = ''
      this.kmEditBookingId = row.id
      this.kmEditField = field
      const current = field === 'kmStandStart' ? row.kmStandStart : row.kmStandEnd
      this.kmEditOriginal = String(current)
      this.kmEditValue = String(current)
    },
    cancelKmEdit() {
      this.kmEditBookingId = null
      this.kmEditField = ''
      this.kmEditValue = ''
      this.kmEditOriginal = ''
    },
    async saveKmEdit() {
      if (this.kmEditSaving || this.kmEditBookingId === null) return
      const bookingId = this.kmEditBookingId
      const field = this.kmEditField
      const raw = String(this.kmEditValue).trim()
      if (raw === '' || Number.isNaN(Number(raw)) || Number(raw) < 0) {
        this.kmEditError = 'Angiv et gyldigt km-tal (0 eller højere).'
        return
      }
      const km = parseInt(raw, 10)
      if (String(km) === this.kmEditOriginal) {
        this.cancelKmEdit()
        return
      }

      this.kmEditSaving = true
      this.kmEditError = ''
      try {
        await saveKmCorrection({ bookingId, field, km })
        this.cancelKmEdit()
        await this.loadUsages()
      } catch (error) {
        this.kmEditError = (error && error.message) || 'Kunne ikke gemme km-rettelse.'
      } finally {
        this.kmEditSaving = false
      }
    },
    resetFilters() {
      const prev = previousMonthParts()
      this.selectedYear = prev.year
      this.selectedMonth = prev.month
      this.selectedVehicle = ''
      if (!this.isSharedView) {
        this.selectedAccount = ''
      }
      this.onFiltersChange()
    },
    onFiltersChange() {
      this.syncFiltersToQuery()
      this.loadUsages()
    },
    applyQueryToFilters() {
      const query = this.$route.query || {}
      const year = parseInt(query.year, 10)
      const month = parseInt(query.month, 10)
      const value = periodValue(year, month)
      const allowed = this.periodOptions.some((period) => period.value === value)
      if (allowed) {
        this.selectedYear = year
        this.selectedMonth = month
      } else {
        const prev = previousMonthParts()
        this.selectedYear = prev.year
        this.selectedMonth = prev.month
      }
      this.selectedVehicle = query.vehicle ? String(query.vehicle) : ''
      this.selectedAccount = query.account ? String(query.account) : ''
    },
    syncFiltersToQuery() {
      const query = {
        year: String(this.selectedYear),
        month: String(this.selectedMonth)
      }
      if (this.selectedVehicle) {
        query.vehicle = this.selectedVehicle
      }
      if (this.selectedAccount) {
        query.account = this.selectedAccount
      }

      const current = this.$route.query || {}
      const same = String(current.year || '') === query.year
        && String(current.month || '') === query.month
        && String(current.vehicle || '') === String(query.vehicle || '')
        && String(current.account || '') === String(query.account || '')
      if (same) return

      this.$router.replace({ query }).catch(() => {})
    },
    async loadUsages() {
      if (!this.canView) return
      this.loading = true
      this.loadError = ''
      try {
        const data = await fetchConsumption({
          start: this.startParam,
          vehicle: this.selectedVehicle,
          account: this.accountParamForApi
        })
        this.usages = data.usages
        this.vehicles = data.vehicles
        this.accounts = data.accounts
        this.subscription = data.subscription
        this.loadedOnce = true
      } catch (error) {
        this.loadError = (error && error.message) || 'Kunne ikke hente forbrug.'
        this.usages = []
      } finally {
        this.loading = false
      }
    }
  }
})
</script>

<style scoped>
.forbrug-page {
  width: 100%;
  max-width: none;
  margin-bottom: 2rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
</style>
